import React, { useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { packages, whatsappLink } from '../mock';
import { ArrowLeft, Calendar, Check, MapPin, MessageCircle, Phone, IndianRupee, Users } from 'lucide-react';

export default function PackageDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const pkg = packages.find((p) => p.id === id);

  useEffect(() => { window.scrollTo(0, 0); }, [id]);

  if (!pkg) {
    return (
      <div>
        <Header />
        <div className="max-w-4xl mx-auto px-5 py-24 text-center">
          <h1 className="font-serif text-3xl text-navy font-bold">Package not found</h1>
          <button onClick={() => navigate('/packages')} className="btn-orange mt-6">Back to Packages</button>
        </div>
        <Footer />
      </div>
    );
  }

  const related = packages.filter((p) => p.category === pkg.category && p.id !== pkg.id).slice(0, 3);

  return (
    <div className="bg-white">
      <Header />

      {/* Hero */}
      <section className="relative">
        <div className="h-[52vh] min-h-[380px] relative overflow-hidden">
          <img src={pkg.image} alt={pkg.title} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(6,18,40,0.55) 0%, rgba(6,18,40,0.85) 100%)' }} />
          <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 h-full flex flex-col justify-end pb-12 text-white">
            <Link to="/packages" className="inline-flex items-center gap-2 text-sm text-blue-100 hover:text-orange-300 mb-4"><ArrowLeft className="w-4 h-4" /> All Packages</Link>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">{pkg.duration}</span>
              <span className="bg-white/15 border border-white/20 text-xs px-3 py-1 rounded-full uppercase tracking-wider">{pkg.category}</span>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl font-bold">{pkg.title}</h1>
            <p className="mt-3 max-w-3xl text-blue-50/85 flex items-start gap-2"><MapPin className="w-4 h-4 mt-1 text-orange-300 shrink-0" />{pkg.highlights}</p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            {/* Quick facts */}
            <div className="grid sm:grid-cols-3 gap-4 mb-10">
              <div className="rounded-xl border border-slate-100 p-4 bg-slate-50"><div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider"><Calendar className="w-3.5 h-3.5" />Duration</div><div className="mt-1 font-bold text-navy text-lg">{pkg.duration}</div></div>
              <div className="rounded-xl border border-slate-100 p-4 bg-slate-50"><div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider"><IndianRupee className="w-3.5 h-3.5" />Starting Price</div><div className="mt-1 font-bold text-navy text-lg">₹{pkg.priceFrom.toLocaleString('en-IN')} <span className="text-xs font-normal text-slate-500">/ pp</span></div></div>
              <div className="rounded-xl border border-slate-100 p-4 bg-slate-50"><div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider"><Users className="w-3.5 h-3.5" />Group Size</div><div className="mt-1 font-bold text-navy text-lg">2 - 45 pax</div></div>
            </div>

            {/* Inclusions */}
            <h2 className="font-serif text-2xl font-bold text-navy">Package Inclusions</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {pkg.inclusions.map((inc) => (
                <span key={inc} className="inline-flex items-center gap-1.5 text-sm font-medium bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-md"><Check className="w-4 h-4" />{inc}</span>
              ))}
            </div>

            {/* Itinerary */}
            <h2 className="font-serif text-2xl font-bold text-navy mt-12">Day-wise Itinerary</h2>
            <div className="mt-6 relative border-l-2 border-orange-200 pl-6 space-y-6">
              {pkg.itinerary.map((d) => (
                <div key={d.day} className="relative">
                  <div className="absolute -left-[34px] w-6 h-6 rounded-full bg-orange-500 text-white text-[11px] font-bold flex items-center justify-center shadow">{d.day}</div>
                  <div className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm">
                    <div className="text-xs uppercase tracking-wider text-orange-500 font-semibold">Day {d.day}</div>
                    <h3 className="mt-1 font-serif text-lg font-bold text-navy">{d.title}</h3>
                    <p className="mt-2 text-slate-600 leading-relaxed text-[15px]">{d.details}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Notes */}
            <div className="mt-10 rounded-xl border border-amber-100 bg-amber-50 p-5 text-sm text-amber-900">
              <strong>Note:</strong> Above itinerary is indicative and can be customized as per your travel dates, group size and budget. VIP darshan / helicopter / air / rail tickets can be arranged on request. Prices exclude 5% GST unless mentioned.
            </div>
          </div>

          {/* Booking Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24 rounded-2xl bg-navy text-white p-7 shadow-xl" style={{ backgroundColor: '#0b1e3d' }}>
              <div className="text-sm text-blue-100/70">Starting from</div>
              <div className="text-3xl font-bold text-orange-400 mt-1">₹{pkg.priceFrom.toLocaleString('en-IN')}<span className="text-sm text-blue-100/70 font-normal">/person</span></div>
              <div className="mt-4 text-sm text-blue-100/80">On twin sharing basis • {pkg.duration}</div>

              <div className="mt-6 space-y-3">
                <a href={whatsappLink(`Hello, I want quote for ${pkg.title} package`)} target="_blank" rel="noreferrer" className="btn-orange w-full inline-flex items-center justify-center gap-2"><MessageCircle className="w-4 h-4" /> Get Quote on WhatsApp</a>
                <a href={whatsappLink(`I want to book ${pkg.title} package`)} target="_blank" rel="noreferrer" className="w-full inline-flex items-center justify-center gap-2 bg-white text-navy rounded-full py-2.5 font-semibold hover:bg-orange-100 transition-colors">Book Now</a>
                <a href="tel:+917042707114" className="w-full inline-flex items-center justify-center gap-2 border border-white/25 rounded-full py-2.5 font-semibold hover:bg-white/10 transition-colors"><Phone className="w-4 h-4" /> +91 70427 07114</a>
              </div>

              <div className="mt-6 pt-6 border-t border-white/10 text-xs text-blue-100/70 space-y-2">
                <div className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-orange-400" />Instant confirmation</div>
                <div className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-orange-400" />24x7 WhatsApp support</div>
                <div className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-orange-400" />Customization available</div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="section bg-slate-50">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <h2 className="font-serif text-3xl font-bold text-navy">You may also like</h2>
            <div className="grid md:grid-cols-3 gap-6 mt-8">
              {related.map((p) => (
                <Link key={p.id} to={`/packages/${p.id}`} className="rounded-2xl overflow-hidden bg-white border border-slate-100 card-lift block">
                  <div className="h-44 img-zoom"><img src={p.image} alt={p.title} className="w-full h-full object-cover" /></div>
                  <div className="p-5">
                    <div className="text-xs text-orange-500 font-semibold">{p.duration}</div>
                    <h3 className="font-serif text-lg font-bold text-navy mt-1">{p.title}</h3>
                    <p className="text-sm text-slate-500 mt-1 line-clamp-2">{p.highlights}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
